package com.patronat.top.client.presentation.map

import androidx.lifecycle.viewModelScope
import com.patronat.top.client.domain.venues.GetClientVenuesUseCase
import com.patronat.top.client.location.ClientGeoLocation
import com.patronat.top.client.location.ClientLocationProvider
import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.domain.models.DomainResult
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.VenueFilterRequest
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.VenueStatus
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.sin
import kotlin.math.sqrt
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_map_load_error
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class ClientMapViewModel(
    private val alertController: AlertController,
    private val getClientVenuesUseCase: GetClientVenuesUseCase,
    private val locationProvider: ClientLocationProvider
) : BaseViewModel<ClientMapViewState, ClientMapEvent, ClientMapEffect>(
    initialState = ClientMapViewState()
) {
    private var searchJob: Job? = null

    init {
        refreshLocationAndLoadVenues()
    }

    override fun onEvent(event: ClientMapEvent) {
        when (event) {
            ClientMapEvent.OnRefresh -> refreshLocationAndLoadVenues()

            is ClientMapEvent.OnSearchChanged -> {
                updateState { it.copy(searchQuery = event.value) }
                searchJob?.cancel()
                searchJob = viewModelScope.launch {
                    delay(SearchDebounceMillis)
                    loadVenues()
                }
            }

            is ClientMapEvent.OnMarkerClicked -> selectVenue(event.venueId, collapseList = true)

            ClientMapEvent.OnMapClicked -> {
                updateState { it.copy(selectedVenueId = null) }
            }

            ClientMapEvent.OnToggleListClicked -> {
                updateState { state ->
                    state.copy(
                        isListExpanded = !state.isListExpanded,
                        selectedVenueId = if (state.isListExpanded) state.selectedVenueId else null
                    )
                }
            }

            is ClientMapEvent.OnMapLoadFailed -> {
                updateState {
                    it.copy(mapErrorMessage = event.reason ?: "MapLibre load failed")
                }
            }

            ClientMapEvent.OnMapLoadFinished -> {
                updateState { it.copy(mapErrorMessage = null) }
            }
        }
    }

    private fun refreshLocationAndLoadVenues() {
        viewModelScope.launch {
            val location = locationProvider.getCurrentLocation()
            updateState { state ->
                state.copy(
                    userLocation = location,
                    cameraTarget = location?.toCameraTarget(DefaultMapZoom) ?: state.cameraTarget
                )
            }
            loadVenues()
        }
    }

    private fun loadVenues() {
        searchJob?.cancel()

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            request = { loadPublicVenues(currentState.searchQuery) },
            onError = { _, message ->
                alertController.show(
                    title = UiText.StringResource(DesignRes.string.error_alert_title),
                    message = message?.let(UiText::DynamicString)
                        ?: UiText.StringResource(Res.string.client_map_load_error),
                    type = NotificationType.ERROR
                )
            },
            onSuccess = { loadedVenues ->
                updateState { state ->
                    val mappableVenues = loadedVenues.filter { it.location.coordinates != null }
                    val selectedVenueId = state.selectedVenueId
                        ?.takeIf { id -> mappableVenues.any { it.id == id } }
                    state.copy(
                        venues = mappableVenues,
                        markers = mappableVenues.mapNotNull { it.toMapMarker() },
                        selectedVenueId = selectedVenueId,
                        cameraTarget = state.resolveCameraTarget(mappableVenues, selectedVenueId)
                    )
                }
            }
        )
    }

    private fun selectVenue(venueId: String, collapseList: Boolean) {
        val marker = currentState.markers.firstOrNull { it.venueId == venueId } ?: return
        updateState {
            it.copy(
                selectedVenueId = venueId,
                isListExpanded = if (collapseList) false else it.isListExpanded,
                cameraTarget = ClientMapCameraTarget(
                    latitude = marker.latitude,
                    longitude = marker.longitude,
                    zoom = SelectedMarkerZoom
                )
            )
        }
    }

    private suspend fun loadPublicVenues(query: String): DomainResult<List<VenueResponse>> {
        val connected = loadVenuesByStatus(VenueStatus.CONNECTED, query)
        val addedByModerator = loadVenuesByStatus(VenueStatus.ADDED_BY_MODERATOR, query)

        val venues = buildList {
            if (connected is DomainResult.Success) addAll(connected.data)
            if (addedByModerator is DomainResult.Success) addAll(addedByModerator.data)
        }

        if (venues.isNotEmpty() || connected is DomainResult.Success || addedByModerator is DomainResult.Success) {
            return DomainResult.Success(
                venues
                    .distinctBy(VenueResponse::id)
                    .sortByDistanceIfPossible(currentState.userLocation)
            )
        }

        return connected as DomainResult.Error
    }

    private suspend fun loadVenuesByStatus(
        status: VenueStatus,
        query: String
    ): DomainResult<List<VenueResponse>> {
        val response = getClientVenuesUseCase.execute(
            VenueFilterRequest(
                page = 1,
                pageSize = PageSize,
                query = query.trim().ifBlank { null },
                status = status,
                latitude = currentState.userLocation?.latitude,
                longitude = currentState.userLocation?.longitude
            )
        )

        return when (response) {
            is DomainResult.Success -> DomainResult.Success(response.data.items)
            is DomainResult.Error -> response
        }
    }

    private fun ClientMapViewState.resolveCameraTarget(
        venues: List<VenueResponse>,
        selectedVenueId: String?
    ): ClientMapCameraTarget {
        val selectedMarker = selectedVenueId?.let { id ->
            venues.firstOrNull { it.id == id }?.toMapMarker()
        }
        if (selectedMarker != null) {
            return ClientMapCameraTarget(
                latitude = selectedMarker.latitude,
                longitude = selectedMarker.longitude,
                zoom = SelectedMarkerZoom
            )
        }

        userLocation?.let { return it.toCameraTarget(DefaultMapZoom) }

        val firstMarker = venues.firstOrNull()?.toMapMarker()
        return firstMarker?.let {
            ClientMapCameraTarget(
                latitude = it.latitude,
                longitude = it.longitude,
                zoom = DefaultMapZoom
            )
        } ?: cameraTarget
    }

    private fun VenueResponse.toMapMarker(): ClientMapMarker? {
        val coordinates = location.coordinates ?: return null
        return ClientMapMarker(
            venueId = id,
            title = name,
            group = category.group,
            latitude = coordinates.latitude,
            longitude = coordinates.longitude
        )
    }

    private fun ClientGeoLocation.toCameraTarget(zoom: Double): ClientMapCameraTarget {
        return ClientMapCameraTarget(
            latitude = latitude,
            longitude = longitude,
            zoom = zoom
        )
    }

    private fun List<VenueResponse>.sortByDistanceIfPossible(location: ClientGeoLocation?): List<VenueResponse> {
        if (location == null) {
            return sortedByDescending { it.updatedAt.toEpochMilliseconds() }
        }

        return sortedWith(
            compareBy<VenueResponse> { venue ->
                venue.distanceTo(location) ?: Double.POSITIVE_INFINITY
            }.thenByDescending { it.updatedAt.toEpochMilliseconds() }
        )
    }

    private fun VenueResponse.distanceTo(location: ClientGeoLocation): Double? {
        val coordinates = this.location.coordinates ?: return null
        return haversineDistanceKm(
            latitude1 = location.latitude,
            longitude1 = location.longitude,
            latitude2 = coordinates.latitude,
            longitude2 = coordinates.longitude
        )
    }

    private fun haversineDistanceKm(
        latitude1: Double,
        longitude1: Double,
        latitude2: Double,
        longitude2: Double
    ): Double {
        val latitudeDelta = (latitude2 - latitude1).toRadians()
        val longitudeDelta = (longitude2 - longitude1).toRadians()
        val startLatitude = latitude1.toRadians()
        val endLatitude = latitude2.toRadians()
        val a = sin(latitudeDelta / 2).pow(2) +
            cos(startLatitude) * cos(endLatitude) * sin(longitudeDelta / 2).pow(2)

        return EarthRadiusKm * 2 * atan2(sqrt(a), sqrt(1 - a))
    }

    private fun Double.toRadians(): Double = this * PI / 180.0

    private companion object {
        const val PageSize = 200
        const val SearchDebounceMillis = 350L
        const val EarthRadiusKm = 6371.0
    }
}
